import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { AllCommunityModule, themeAlpine } from 'ag-grid-community';
import { AgGridProvider, AgGridReact } from 'ag-grid-react';
import { useState, useRef, useContext } from 'react';
import CircleIcon from '@mui/icons-material/Circle';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import ConnectedTvIcon from '@mui/icons-material/ConnectedTv';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ParkIcon from '@mui/icons-material/Park';
import ParkOutlinedIcon from '@mui/icons-material/ParkOutlined';
import { useOutletContext } from 'react-router';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import MarkChatUnreadIcon from '@mui/icons-material/MarkChatUnread';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';

import { ClinicRxContext } from '../../App';
import {
  translationMap,
  modalStyle,
  alertStatusMap,
  indicatorsMap,
  wasAlertUnclaimed,
} from '.';
import Cards from './Cards';
import UnclaimModal from './UnclaimModal';
import CloseOutModal from './CloseOutModal';
import ChatRoom from './ChatRoom';
import { createHistoryAction, formulateUserObject } from '../custom';
import { getServerURL } from '../../utils';
import {
  UPDATE_DISCHARGE_DATE,
  CHANGE_APPOINTMENT_DATE,
  UPDATE_ALERT_STATUS,
} from '../../dispatchActionTypes';

const DISCHARGE_DATE = 'dischargeDate';
const INCREMENT = 'increment';
const DECREMENT = 'decrement';
const ENGLISH = 'English';
const SERVER_URL = getServerURL();

const modules = [AllCommunityModule];

export default function Dashboard() {
  const { state, dispatch, fetchProtected } = useContext(ClinicRxContext);
  const {
    dashboardFeatures: {
      emitterFns: {
        emitUnclaim,
        emitUpdateAndSchedule,
        emitUpdateStatus,
        emitDischargeDateChange,
        emitAppointmentDateChange,
      },
    },
    chatRoomFeatures: {
      state: { unreadMessages },
    },
  } = useOutletContext();

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isAlertStatusEditModalLoading, setIsAlertStatusEditModalLoading] =
    useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [editedDischargeDate, setEditedDischargeDate] = useState(null);
  const [appointmentDate, setAppointmentDate] = useState(dayjs());
  const [isUnclaimModalOpen, setIsUnclaimModalOpen] = useState(false);
  const [isCloseOutModalOpen, setIsCloseOutModalOpen] = useState(false);
  const [editedAppointmentDate, setEditedAppointmentDate] = useState(dayjs());
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const selectedRowRef = useRef(null);
  const gridRef = useRef(null);
  const alertToClaimRef = useRef(null);
  const dischargeDateToEditRef = useRef(null);

  const userStates = {
    isClinic: state.user.userType === 2,
    isNotClinic: state.user.userType !== 2,
    isLocation: state.user.userType === 1,
    isNotLocation: state.user.userType !== 1,
    isAdmin: state.user.userType === 0,
    isPeer: state.user.userType === 3,
  };

  const isUnreadMessages = () => {
    if (Object.keys(unreadMessages).length === 0) return false;
    return (
      Object.values(unreadMessages).reduce(
        (total, current) => total + current,
        0,
      ) > 0
    );
  };

  const handleOpenDetailsModal = (selectedNode) => {
    selectedRowRef.current = selectedNode;
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    selectedRowRef.current.setSelected(false);
    selectedRowRef.current = null;
    setIsDetailsModalOpen(false);
  };

  const handleCardClick = (option, idx) => {
    if (activeCard === idx) {
      setActiveCard(null);
    } else {
      setActiveCard(idx);
    }
  };

  const filterCardData = () => {
    if (activeCard === null) return state.alertData;
    const filteredData = state.alertData.filter((entry) => {
      return entry.alertStatus === activeCard;
    });
    return filteredData;
  };

  const handleOpen = (node) => {
    alertToClaimRef.current = node;
    setOpen(true);
  };

  const handleClose = () => {
    alertToClaimRef.current = null;
    setAppointmentDate(dayjs());
    setIsAlertStatusEditModalLoading(false);
    setEditedAppointmentDate(dayjs());
    setOpen(false);
  };

  const handleOpenDialog = (e) => {
    dischargeDateToEditRef.current = e.node;
    setEditedDischargeDate(dayjs(e.value, 'MM/DD/YYYY'));
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    dischargeDateToEditRef.current = null;
    setEditedDischargeDate(null);
    setOpenDialog(false);
  };

  const getAlertStatusChipColor = (alertData) => {
    if (!alertData) return 'primary';
    switch (alertData.alertStatus) {
      case 0: {
        if (wasAlertUnclaimed(alertData.actionHistory.actions)) return 'info';
        const now = dayjs();
        const alertDateObj = dayjs(alertData.alertDate, 'MM/DD/YYYY');
        const diff = now.diff(alertDateObj, 'days');
        if (diff >= 1) {
          return 'primary';
        }
        return 'warning';
      }
      case 1: {
        return 'secondary';
      }
      case 2: {
        if (alertData.isUnrouteable) {
          return 'error';
        }
        return 'success';
      }
      default: {
        return 'success';
      }
    }
  };

  const getFlagTitle = (flag) => {
    switch (flag) {
      case 'T': {
        return 'Telehealth';
      }
      case 'X': {
        return 'Existing Patient';
      }
      case 'P': {
        return 'Peer Requested';
      }
      case 'W-A': {
        return 'Warning - appointment date passed';
      }
      case 'W-C': {
        return 'Warning - appointment close to discharge date';
      }
      case 'W-I': {
        return 'Warning - discharge date is after appointment date';
      }
      default: {
        return '';
      }
    }
  };

  const yesNoData = (params) => {
    return (
      <Box>
        <Typography
          sx={{ fontWeight: 'bolder', color: params.value ? 'green' : 'red' }}
        >
          {params.value ? 'Y' : 'N'}
        </Typography>
      </Box>
    );
  };

  const generateColDefs = () => {
    const facilityIdx = 8;
    const facility = [
      {
        field: 'facilityContact.facilityContactOrganization',
        headerName: 'Facility',
        width: 250,
      },
    ];
    const cols = [
      {
        field: 'flags',
        width: 100,
        cellRenderer: (params) => {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '5px',
              }}
            >
              {params.value.map((flag) => {
                const flagMap = {
                  T: ConnectedTvIcon,
                  P: LocalParkingIcon,
                  X: ParkIcon,
                  'W-C': ReportProblemIcon,
                  'W-A': PriorityHighIcon,
                  'W-I': PriorityHighIcon,
                };
                const Icon = flagMap[flag];
                return (
                  <Tooltip
                    key={flag}
                    title={getFlagTitle(flag)}
                  >
                    <Icon sx={{ color: 'primary.dark' }} />
                  </Tooltip>
                );
              })}
            </Box>
          );
        },
        valueFormatter: (params) => {
          return Array.isArray(params.value) && params.value.length > 0
            ? params.value.join(' ')
            : '';
        },
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
        },
      },
      { field: 'alertID', headerName: 'Alert ID', width: 100 },
      {
        field: 'alertStatus',
        headerName: 'Action',
        cellRenderer: (params) => {
          const isCursorPointer = () => {
            if (params.value >= 3) return false;
            if (userStates.isClinic) return true;
            if (userStates.isAdmin && params.data.isUnrouteable) return true;
            return false;
          };
          return (
            <Chip
              label={translationMap[String(params.value)]}
              color={getAlertStatusChipColor(params.data)}
              sx={{
                cursor: isCursorPointer() ? 'pointer' : 'auto',
              }}
              onClick={() => {
                if (params.value >= 3) return;
                if (
                  userStates.isClinic ||
                  (userStates.isAdmin && params.data.isUnrouteable)
                )
                  handleOpen(params.node);
              }}
            />
          );
        },
        width: 150,
      },
      {
        field: 'alertDate',
        headerName: 'Alert Date',
        width: 150,
        sort: 'desc',
      },
      {
        field: 'dischargeDate',
        headerName: 'Discharge Date',
        width: 150,
        cellStyle: {
          cursor: userStates.isLocation ? 'pointer' : 'auto',
        },
        cellRenderer: (params) => {
          const wasEdited = params.data.actionHistory.actions.some(
            (element) => element.action === 'edit-discharge-date',
          );
          if (wasEdited) {
            return <em>{params.value}</em>;
          }
          return params.value;
        },
      },
      {
        field: 'daysLapsed',
        headerName: 'Days Lapsed',
        cellRenderer: (params) => {
          const getCircleColor = (index) => {
            if (index < 2) return 'success';
            if (index < 5) return 'warning';
            return 'error';
          };
          return (
            <Tooltip
              title={`${params.value < 0 ? 0 : params.value} days lapsed`}
            >
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                {Array.from({ length: 7 }).map((element, idx) => {
                  return (
                    <CircleIcon
                      key={idx}
                      sx={{ fontSize: '14px' }}
                      color={
                        idx >= params.value ? 'disabled' : getCircleColor(idx)
                      }
                    />
                  );
                })}
              </Box>
            </Tooltip>
          );
        },
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
        },
      },
      {
        field: 'alertType',
        headerName: 'Type',
        cellRenderer: (params) => {
          const alertTypeMap = {
            SU: 'Substance Use',
            MH: 'Mental Health',
            CO: 'Co-Occurring',
          };
          return <Typography>{alertTypeMap[params.value]}</Typography>;
        },
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
        },
      },
      {
        field: 'diagnosisCodes',
        valueFormatter: (params) => {
          return Array.isArray(params.value) && params.value.length > 0
            ? params.value.map((element) => element.code).join(', ')
            : '';
        },
        cellRenderer: (params) => {
          if (!params.valueFormatted) {
            return <i>None</i>;
          }
          return (
            <Tooltip
              title={params.value
                .map((element) => element.description)
                .join(' & ')}
            >
              {params.valueFormatted}
            </Tooltip>
          );
        },
        headerName: 'Diagnosis Code',
      },
      {
        field: 'facilityContact.facilityContactFullName',
        headerName: 'Hospital Contact',
      },
      {
        field: 'facilityContact.facilityContactPhoneNumber',
        headerName: 'Hospital Contact Phone',
      },
      { field: 'insuranceProvider', headerName: 'Insurance' },
    ];
    if (userStates.isLocation) return cols;
    return [
      ...cols.slice(0, facilityIdx),
      ...facility,
      ...cols.slice(facilityIdx),
    ];
  };

  const handleEditStatus = async (direction, historyReason) => {
    const currentAlertStatus = alertToClaimRef.current.data.alertStatus;
    let nextAlertStatus;
    if (direction === INCREMENT) {
      nextAlertStatus = currentAlertStatus + 1;
    } else {
      nextAlertStatus = currentAlertStatus - 1;
    }
    const isUnclaiming = currentAlertStatus === 1 && nextAlertStatus === 0;
    const isScheduling = currentAlertStatus === 1 && nextAlertStatus === 2;
    const historyEntry = createHistoryAction(
      'edit-alert-status',
      formulateUserObject(state.user),
      alertStatusMap[String(currentAlertStatus)],
      alertStatusMap[String(nextAlertStatus)],
      historyReason,
    );
    try {
      const alertData = {
        alertID: alertToClaimRef.current.data.alertID,
        edit: {
          alertStatus: nextAlertStatus,
        },
        historyEntry,
      };
      const response = await fetchProtected((bearerToken) =>
        fetch(`${SERVER_URL}/edit-alert-status`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: bearerToken,
          },
          body: JSON.stringify(alertData),
        }),
      );
      if (!response.ok) {
        setIsSnackbarOpen(true);
        const contentType = response.headers.get('content-type');
        if (contentType === 'application/json') {
          const data = await response.json();
          setSnackbarMessage(data.error.message);
        } else {
          const data = await response.text();
          setSnackbarMessage(data);
        }
      } else {
        const unclaimedClinics =
          alertToClaimRef.current.data.unclaimedClinics ?? [];
        if (isUnclaiming) {
          const dataToSend = {
            ...alertToClaimRef.current.data,
            alertStatus: nextAlertStatus,
            actionHistory: {
              actions: [
                ...alertToClaimRef.current.data.actionHistory.actions,
                historyEntry,
              ],
            },
            existingClinic: null,
            unclaimedClinics: [
              ...unclaimedClinics,
              historyEntry.user.organization,
            ],
            isTreeOnDashboard: false,
          };
          emitUnclaim({ alertData: dataToSend });
        } else if (isScheduling) {
          const formattedAppointmentDate =
            appointmentDate.format('MM/DD/YYYY h:mm A');
          const historyEntryAppointmentDate = createHistoryAction(
            'set-appointment-date',
            formulateUserObject(state.user),
            'Unscheduled',
            formattedAppointmentDate,
          );
          const dataToSend = {
            ...alertData,
            historyEntryAppointmentDate,
            appointmentDate: formattedAppointmentDate,
          };
          emitUpdateAndSchedule(dataToSend);
        } else {
          const dataToSend = {
            ...alertData,
            unclaimedClinics,
            isTreeOnDashboard: alertToClaimRef.current.data.isTreeOnDashboard,
          };
          // Current user doesn't receive emitted event.
          emitUpdateStatus(dataToSend);
        }
        if (!isScheduling) {
          // Update current user state when not
          // unclaiming or scheduling via a direct
          // dispatch instead of circling through socket.
          dispatch({
            type: UPDATE_ALERT_STATUS,
            payload: {
              alertID: alertToClaimRef.current.data.alertID,
              nextAlertStatus,
              historyEntry,
              shouldRemoveFromDashboard: isUnclaiming,
              unclaimedClinics: isUnclaiming
                ? [...unclaimedClinics, historyEntry.user.organization]
                : unclaimedClinics,
            },
          });
        }
      }
    } catch (err) {
      console.log(
        `Error making fetch request to edit-alert-status API: ${err}`,
      );
    }
  };

  const handleCellClicked = (e) => {
    const columnID = e.column.getColId();
    if (columnID === DISCHARGE_DATE && userStates.isLocation) {
      handleOpenDialog(e);
    }
  };

  const handleSelectionChanged = (event) => {
    const selectedNodes = event.api.getSelectedNodes();
    if (selectedNodes.length) {
      handleOpenDetailsModal(selectedNodes[0]);
    }
  };

  const handleEditDischargeDate = () => {
    const historyEntry = createHistoryAction(
      'edit-discharge-date',
      formulateUserObject(state.user),
      dischargeDateToEditRef.current.data.dischargeDate,
      editedDischargeDate.format('MM/DD/YYYY'),
    );
    const dischargeData = {
      alertID: dischargeDateToEditRef.current.data.alertID,
      updatedDischargeDate: editedDischargeDate.format('MM/DD/YYYY'),
      historyEntry,
    };
    emitDischargeDateChange(dischargeData);
    dispatch({
      type: UPDATE_DISCHARGE_DATE,
      payload: dischargeData,
    });
    fetchProtected((bearerToken) =>
      fetch(`${SERVER_URL}/edit-discharge-date`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: bearerToken,
        },
        body: JSON.stringify({
          alertID: dischargeDateToEditRef.current.data.alertID,
          edit: {
            dischargeDate: editedDischargeDate.format('MM/DD/YYYY'),
          },
          historyEntry,
        }),
      }),
    ).catch((e) =>
      console.log(
        `Error making fetch request to edit-discharge-date API: ${e}`,
      ),
    );
    handleCloseDialog();
  };

  const handleSetAppointmentDate = () => {
    const formattedAppointmentDate =
      appointmentDate.format('MM/DD/YYYY h:mm A');
    const historyEntry = createHistoryAction(
      'set-appointment-date',
      formulateUserObject(state.user),
      'Unscheduled',
      formattedAppointmentDate,
    );
    fetchProtected((bearerToken) =>
      fetch(`${SERVER_URL}/set-appointment-date`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: bearerToken,
        },
        body: JSON.stringify({
          alertID: alertToClaimRef.current.data.alertID,
          edit: {
            appointmentDate: formattedAppointmentDate,
          },
          historyEntry,
        }),
      }),
    ).catch((err) => {
      console.log(
        `Error making fetch request to set-appointment-date API: ${err}`,
      );
    });
  };

  const handleEditAlertStatusClick = async (
    alertStatus,
    direction = 'increment',
    historyReason = null,
  ) => {
    setIsAlertStatusEditModalLoading(true);
    await handleEditStatus(direction, historyReason);
    if (alertStatus === 1 && direction === INCREMENT) {
      handleSetAppointmentDate();
    }
    handleClose();
  };

  const handleEditAppointmentDate = async () => {
    const formattedEditedAppointmentDate =
      editedAppointmentDate.format('MM/DD/YYYY h:mm A');
    const historyEntry = createHistoryAction(
      'edit-appointment-date',
      formulateUserObject(state.user),
      alertToClaimRef.current.data.appointmentDate,
      formattedEditedAppointmentDate,
    );
    const alertData = {
      alertID: alertToClaimRef.current.data.alertID,
      edit: {
        appointmentDate: formattedEditedAppointmentDate,
      },
      historyEntry,
    };
    try {
      setIsAlertStatusEditModalLoading(true);
      const response = await fetchProtected((bearerToken) =>
        fetch(`${SERVER_URL}/edit-appointment-date`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: bearerToken,
          },
          body: JSON.stringify(alertData),
        }),
      );
      if (response.ok) {
        emitAppointmentDateChange(alertData);
        dispatch({
          type: CHANGE_APPOINTMENT_DATE,
          payload: {
            alertID: alertToClaimRef.current.data.alertID,
            changedAppointmentDate: formattedEditedAppointmentDate,
            historyEntry,
          },
        });
        handleClose();
      } else {
        setIsSnackbarOpen(true);
        setSnackbarMessage('Error editing appointment date');
      }
    } catch (err) {
      console.log(
        `Error making fetch request to edit-appointment-date API: ${err}`,
      );
    } finally {
      setIsAlertStatusEditModalLoading(false);
    }
  };

  const renderEditAlertStatusModalContent = (alertStatus) => {
    if (alertToClaimRef.current?.data.isUnrouteable) {
      return null;
    }
    switch (alertStatus) {
      case 1: {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                disablePast
                label='Enter Appointment Date and Time'
                value={appointmentDate}
                onChange={(newValue) => {
                  setAppointmentDate(newValue);
                }}
              />
            </LocalizationProvider>
            <Button
              loading={isAlertStatusEditModalLoading}
              color='info'
              variant='contained'
              onClick={() => setIsUnclaimModalOpen(true)}
            >
              Unclaim
            </Button>
          </Box>
        );
      }
      case 2: {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                disablePast
                label='Edit Appointment Date and Time'
                value={editedAppointmentDate}
                onChange={(newValue) => {
                  setEditedAppointmentDate(newValue);
                }}
              />
            </LocalizationProvider>
            <Button
              loading={isAlertStatusEditModalLoading}
              color='info'
              variant='contained'
              onClick={handleEditAppointmentDate}
              disabled={!Boolean(alertToClaimRef.current?.data.appointmentDate)}
            >
              Edit Appointment Date
            </Button>
          </Box>
        );
      }
      default: {
        return null;
      }
    }
  };

  const renderHistoryEntry = (action) => {
    switch (action.action) {
      case 'create-alert': {
        return (
          <Typography>{`New alert created by ${action.user.name} on ${action.timestamp}`}</Typography>
        );
      }
      case 'set-appointment-date': {
        return (
          <Typography>
            Appointment date set for
            <Box
              component='span'
              sx={{ color: 'secondary.main' }}
            >
              {` ${action.altered.to} `}
            </Box>
            by {action.user.name}
          </Typography>
        );
      }
      case 'edit-appointment-date': {
        return (
          <Typography>
            Appointment date edited to
            <Box
              component='span'
              sx={{ color: 'secondary.main' }}
            >
              {` ${action.altered.to} `}
            </Box>
            by {action.user.name}
          </Typography>
        );
      }
      case 'edit-alert-status': {
        const closedOutBecauseOfUnrouteable =
          action.altered.reason === 'Unrouteable';
        if (closedOutBecauseOfUnrouteable) {
          return (
            <Typography>
              On {action.timestamp}, {action.user.name} closed-out the alert
              because it is unrouteable
            </Typography>
          );
        }
      }
      default: {
        return (
          <Typography>
            On {action.timestamp}, {action.user.name} changed "
            {action.altered.from}" to "{action.altered.to}"
            {action.altered.reason && ` because of "${action.altered.reason}"`}
          </Typography>
        );
      }
    }
  };

  const getDetailsModalAlertStatus = (
    alertStatus,
    actions,
    isUnrouteable = false,
  ) => {
    let status;
    switch (alertStatus) {
      case undefined: {
        status = '';
        break;
      }
      case 2: {
        if (isUnrouteable) {
          status = 'Unrouteable';
          break;
        }
      }
      default: {
        status = alertStatusMap[alertStatus];
      }
    }
    return status.toUpperCase();
  };

  const [colDefs, setColDefs] = useState(generateColDefs());

  return (
    <Box
      sx={{
        '.ag-checkbox-input': {
          cursor: 'pointer',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Cards
          activeCard={activeCard}
          handleCardClick={handleCardClick}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '25px',
            paddingRight: '5px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Tooltip title='Chat'>
              {isUnreadMessages() ? (
                <MarkChatUnreadIcon
                  color='primary'
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setIsDrawerOpen((prev) => !prev)}
                />
              ) : (
                <ChatBubbleOutlineOutlinedIcon
                  color='primary'
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setIsDrawerOpen((prev) => !prev)}
                />
              )}
            </Tooltip>
          </Box>
        </Box>
      </Box>
      <AgGridProvider modules={modules}>
        <Box sx={{ height: '666px' }}>
          <AgGridReact
            ref={gridRef}
            rowSelection={{
              mode: 'singleRow',
              checkboxes: true,
              enableClickSelection: false,
            }}
            theme={themeAlpine}
            rowData={filterCardData()}
            columnDefs={colDefs}
            onSelectionChanged={handleSelectionChanged}
            onCellClicked={handleCellClicked}
          />
        </Box>
      </AgGridProvider>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-status-modal'
        aria-describedby='modal-to-update-alert-status'
      >
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <CloseIcon
              sx={{ '&:hover': { cursor: 'pointer' } }}
              onClick={handleClose}
            />
            <Typography
              sx={{ fontWeight: 'bold', textAlign: 'center' }}
            >{`Are you sure you want to ${alertToClaimRef ? (translationMap[String(alertToClaimRef.current?.data.alertStatus)] ?? '').toLowerCase() : ''} this alert?`}</Typography>
            {renderEditAlertStatusModalContent(
              alertToClaimRef.current?.data.alertStatus,
            )}
            <Button
              color='success'
              loading={isAlertStatusEditModalLoading}
              variant='contained'
              onClick={() => {
                const currentAlertStatus =
                  alertToClaimRef.current?.data.alertStatus;
                if (currentAlertStatus === 2) {
                  setIsCloseOutModalOpen(true);
                } else {
                  handleEditAlertStatusClick(
                    alertToClaimRef.current?.data.alertStatus,
                  );
                }
              }}
            >
              Yes
            </Button>
          </Box>
          <UnclaimModal
            isUnclaimModalOpen={isUnclaimModalOpen}
            setIsUnclaimModalOpen={setIsUnclaimModalOpen}
            handleUnclaimClick={(unclaimReason) => {
              setIsUnclaimModalOpen(false);
              handleEditAlertStatusClick(
                alertToClaimRef.current?.data.alertStatus,
                DECREMENT,
                unclaimReason,
              );
            }}
          />
          <CloseOutModal
            isCloseOutModalOpen={isCloseOutModalOpen}
            setIsCloseOutModalOpen={setIsCloseOutModalOpen}
            handleCloseOutClick={(closeOutReason) => {
              setIsCloseOutModalOpen(false);
              handleEditAlertStatusClick(
                alertToClaimRef.current?.data.alertStatus,
                INCREMENT,
                closeOutReason,
              );
            }}
            isUnrouteable={alertToClaimRef.current?.data.isUnrouteable}
          />
        </Box>
      </Modal>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby='edit-discharge-date-dialog'
        aria-describedby='dialog-to-edit-discharge-date'
        role='alertdialog'
      >
        <DialogTitle id='edit-discharge-date-dialog-title'>
          {'Edit Discharge Date?'}
        </DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
        >
          <DialogContentText id='edit-discharge-date-dialog-description'>
            This action will be logged in user history.
          </DialogContentText>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <DatePicker
                label='New Discharge Date'
                value={editedDischargeDate}
                onChange={(newValue) => {
                  setEditedDischargeDate(newValue);
                }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            autoFocus
          >
            Cancel
          </Button>
          <Button onClick={handleEditDischargeDate}>Complete</Button>
        </DialogActions>
      </Dialog>
      <Modal
        open={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        aria-labelledby='alert-details-modal'
        aria-describedby='modal-to-view-alert-details'
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
            }}
          >
            <CloseIcon
              sx={{ '&:hover': { cursor: 'pointer' }, alignSelf: 'flex-end' }}
              onClick={handleCloseDetailsModal}
            />
            <Box
              sx={{
                alignSelf: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: '7px',
                width: '100%',
              }}
            >
              <Typography
                sx={{ fontWeight: 'bold', alignSelf: 'center' }}
              >{`Alert ID #${selectedRowRef.current?.data.alertID}`}</Typography>
              <Chip
                label={getDetailsModalAlertStatus(
                  selectedRowRef.current?.data.alertStatus,
                  selectedRowRef.current?.data.actionHistory.actions,
                  selectedRowRef.current?.data.isUnrouteable,
                )}
                color={getAlertStatusChipColor(selectedRowRef.current?.data)}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '10px',
                  flexWrap: 'wrap',
                }}
              >
                {selectedRowRef.current?.data.existingClinic &&
                  selectedRowRef.current?.data.isTreeOnDashboard &&
                  userStates.isNotClinic && (
                    <Chip
                      key={selectedRowRef.current?.data.preferredLanguage}
                      size='small'
                      variant='outlined'
                      icon={<ParkOutlinedIcon />}
                      label={selectedRowRef.current?.data.existingClinic}
                    />
                  )}
                {Object.entries(
                  selectedRowRef.current?.data.indicators ?? {},
                ).map(([name, isPresent], idx) => {
                  if (isPresent) {
                    return (
                      <Chip
                        key={name}
                        size='small'
                        variant='outlined'
                        label={indicatorsMap[name]}
                      />
                    );
                  }
                })}
                {!!selectedRowRef.current?.data.preferredLanguage &&
                  selectedRowRef.current?.data.preferredLanguage !==
                    ENGLISH && (
                    <Chip
                      key={selectedRowRef.current?.data.preferredLanguage.concat(
                        selectedRowRef.current?.data.alertID,
                      )}
                      size='small'
                      variant='outlined'
                      label={`Preferred Language: ${selectedRowRef.current?.data.preferredLanguage}`}
                    />
                  )}
              </Box>
            </Box>
            <Box>
              <Typography sx={{ textDecoration: 'underline' }}>
                {`Edit History:`}
              </Typography>
              {selectedRowRef.current?.data.actionHistory?.actions.map(
                (action, idx) => {
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '15px',
                          alignItems: 'center',
                        }}
                      >
                        <Chip
                          label={action.action}
                          color={'secondary'}
                          variant='outlined'
                        />
                        <Tooltip
                          title={`${action.user.name} ${action.user.email} ${action.user.organization} ${action.user.phoneNumber}`}
                          placement='right-start'
                        >
                          {renderHistoryEntry(action)}
                        </Tooltip>
                      </Box>
                      <Divider variant='middle' />
                    </Box>
                  );
                },
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
      <ChatRoom
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
      />
    </Box>
  );
}
